/**
 * Publishes Shopify Customer Events after cart mutations so pixels (e.g. Stape)
 * that subscribe to product_added_to_cart / product_removed_from_cart can react.
 *
 * Zusätzlich wird ein GA4/Meta-konformer add_to_cart-Event in window.dataLayer
 * gepusht (GTM). Preise sind NETTO (Listen-Netto pro Stück aus der Line-Property
 * _net_price, Fallback = Brutto aus price/final_price). Vor jedem ecommerce-Push
 * wird { ecommerce: null } gepusht.
 */
window.SAnalytics = (function () {
  'use strict';

  var CURRENCY = 'EUR';

  function currencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || CURRENCY;
  }

  function money(amount) {
    return {
      amount: Number(amount) || 0,
      currencyCode: currencyCode()
    };
  }

  function round2(n) {
    return Math.round((n + Number.EPSILON) * 100) / 100;
  }

  function unitPrice(line) {
    if (!line) return 0;
    return (Number(line.final_price != null ? line.final_price : line.price) || 0) / 100;
  }

  /**
   * Liest den Listen-Netto-Preis pro Stück aus einer /cart(/add).js-Line.
   * Fallback (fehlendes _net_price) = Brutto pro Stück.
   * @param {object} line
   * @returns {number}
   */
  function lineNetUnit(line) {
    if (!line) return 0;
    var net = NaN;
    if (line.properties && line.properties._net_price != null && line.properties._net_price !== '') {
      net = parseFloat(line.properties._net_price);
    }
    if (isNaN(net)) net = unitPrice(line);
    return net;
  }

  function itemName(productTitle, variantTitle) {
    if (variantTitle && variantTitle !== 'Default Title') {
      return productTitle + ' - ' + variantTitle;
    }
    return productTitle;
  }

  function imageSrc(line) {
    if (!line) return '';
    if (typeof line.image === 'string') return line.image;
    if (line.image && line.image.src) return line.image.src;
    if (line.featured_image && line.featured_image.url) return line.featured_image.url;
    return '';
  }

  function cartLineFromAjax(line, quantity) {
    var qty = Number(quantity) || Number(line.quantity) || 1;
    var unit = unitPrice(line);
    var variantTitle = line.variant_title && line.variant_title !== 'Default Title'
      ? line.variant_title
      : (line.variant_title || null);

    return {
      cost: {
        totalAmount: money(unit * qty)
      },
      merchandise: {
        id: String(line.variant_id || line.id || ''),
        title: variantTitle,
        untranslatedTitle: variantTitle,
        sku: line.sku || null,
        price: money(unit),
        image: { src: imageSrc(line) },
        product: {
          id: String(line.product_id || ''),
          title: line.product_title || '',
          untranslatedTitle: line.product_title || '',
          vendor: line.vendor || '',
          type: line.product_type || null,
          url: line.url || null
        }
      },
      quantity: qty
    };
  }

  function dataLayerPush(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
  }

  /**
   * Baut den GA4/Meta-add_to_cart-Push aus einer /cart/add.js-Response.
   * Unterstützt Einzel-Adds (Line-Objekt) und Multi-Adds ({ items: [...] }).
   * @param {object} data - Response von /cart/add.js.
   * @param {number} [requestedQty] - Angeforderte Menge (nur für Einzel-Adds relevant).
   */
  function pushAddToCartDataLayer(data, requestedQty) {
    if (!data) return;
    var lines = data.items && Array.isArray(data.items) ? data.items : [data];
    var gaItems = [];
    var fbContents = [];
    var total = 0;

    lines.forEach(function (line) {
      var qty = lines.length > 1
        ? (Number(line.quantity) || 1)
        : (Number(requestedQty) || Number(line.quantity) || 1);
      var net = lineNetUnit(line);
      var sku = String(line.sku || '');
      total += net * qty;

      gaItems.push({
        item_id: sku,
        item_name: itemName(line.product_title, line.variant_title),
        item_brand: line.vendor,
        item_variant: line.variant_title || null,
        price: round2(net),
        quantity: qty
      });
      fbContents.push({ id: sku, quantity: qty, item_price: round2(net) });
    });

    total = round2(total);

    dataLayerPush({
      event: 'add_to_cart',
      ecommerce: {
        currency: CURRENCY,
        value: total,
        items: gaItems
      },
      fb_event: 'AddToCart',
      fb_contents: fbContents,
      fb_content_type: 'product',
      fb_value: total,
      fb_currency: CURRENCY
    });
  }

  /**
   * Baut den GA4/Meta-view_cart-Push aus einem /cart.js-Cart-Objekt.
   * Netto NACH Rabatt, berechnet über das Verhältnis netto/brutto pro Line
   * (kein Steuersatz-basierter Ansatz): netto_nach_rabatt =
   * brutto_nach_rabatt × (listen_netto / brutto_pro_stück). Fallback ratio = 1.
   * @param {object} cart - Response von /cart.js.
   */
  function pushViewCartDataLayer(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    var gaItems = [];
    var fbContents = [];
    var total = 0;

    cart.items.forEach(function (item) {
      var qty = Number(item.quantity) || 0;

      var netListUnit = NaN;
      if (item.properties && item.properties._net_price != null && item.properties._net_price !== '') {
        netListUnit = parseFloat(item.properties._net_price);
      }

      var grossUnit = (Number(item.original_price) || 0) / 100;
      var ratio = 1;
      if (!isNaN(netListUnit) && grossUnit > 0) ratio = netListUnit / grossUnit;
      if (isNaN(netListUnit)) netListUnit = grossUnit;

      var grossLineAfter = (Number(item.final_line_price) || 0) / 100;
      var netLineAfter = grossLineAfter * ratio;
      var netUnitAfter = qty > 0 ? netLineAfter / qty : netLineAfter;
      var discountUnit = round2(netListUnit - netUnitAfter);

      total += netLineAfter;

      gaItems.push({
        item_id: String(item.sku || ''),
        item_name: itemName(item.product_title, item.variant_title),
        item_brand: item.vendor,
        item_variant: item.variant_title || null,
        price: round2(netListUnit),
        quantity: qty,
        discount: discountUnit
      });
      fbContents.push({
        id: String(item.sku || ''),
        quantity: qty,
        item_price: round2(netUnitAfter)
      });
    });

    total = round2(total);

    dataLayerPush({
      event: 'view_cart',
      ecommerce: {
        currency: CURRENCY,
        value: total,
        items: gaItems
      },
      fb_event: 'ViewCart',
      fb_contents: fbContents,
      fb_content_type: 'product',
      fb_value: total,
      fb_currency: CURRENCY
    });
  }

  /**
   * Pusht view_item_list (GA4) + ViewCategory (Meta).
   * Erwartet vorbereitete Items – Werte kommen aus Liquid (Listen-Netto aus
   * dem Varianten-Metafield custom.net_price).
   *
   * @param {object} opts
   * @param {string|number} opts.listId - z.B. Collection-ID.
   * @param {string} opts.listName - z.B. Collection-Titel.
   * @param {Array<{item_id:string,item_name:string,item_brand?:string,item_variant?:string,price:number,index?:number}>} opts.items
   */
  function pushViewItemListDataLayer(opts) {
    if (!opts || !Array.isArray(opts.items) || opts.items.length === 0) return;

    var items = opts.items.map(function (it, idx) {
      return {
        item_id: String(it.item_id || ''),
        item_name: it.item_name || '',
        item_brand: it.item_brand || undefined,
        item_variant: it.item_variant || undefined,
        item_list_id: opts.listId != null ? String(opts.listId) : undefined,
        item_list_name: opts.listName || undefined,
        price: round2(Number(it.price) || 0),
        index: it.index != null ? Number(it.index) : idx + 1
      };
    });

    dataLayerPush({
      event: 'view_item_list',
      ecommerce: {
        item_list_id: opts.listId != null ? String(opts.listId) : undefined,
        item_list_name: opts.listName || undefined,
        items: items
      },
      fb_event: 'ViewCategory',
      fb_content_category: opts.listName || undefined
    });
  }

  /**
   * Lädt /cart.js und pusht anschließend view_cart.
   */
  function viewCartFetch() {
    var base = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
    fetch(base + '.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) { pushViewCartDataLayer(cart); })
      .catch(function () {});
  }

  function publish(eventName, line, quantity) {
    if (!line) return;
    if (!window.Shopify || !Shopify.analytics || typeof Shopify.analytics.publish !== 'function') return;

    try {
      // Top-level cartLine fields (Shopify staff workaround for custom ATC flows)
      Shopify.analytics.publish(eventName, cartLineFromAjax(line, quantity));
    } catch (e) {
      // publish can throw if the analytics bridge is unavailable
    }
  }

  return {
    productAddedToCart: function (data, quantity) {
      pushAddToCartDataLayer(data, quantity);

      // Shopify Customer Events (Web Pixels) weiterhin publizieren.
      var lines = data && data.items && Array.isArray(data.items) ? data.items : [data];
      lines.forEach(function (line) {
        var qty = lines.length > 1
          ? (Number(line && line.quantity) || 1)
          : (Number(quantity) || Number(line && line.quantity) || 1);
        publish('product_added_to_cart', line, qty);
      });
    },
    productRemovedFromCart: function (line, quantity) {
      publish('product_removed_from_cart', line, quantity);
    },
    viewCart: pushViewCartDataLayer,
    viewCartFetch: viewCartFetch,
    viewItemList: pushViewItemListDataLayer
  };
}());

/* ---------------------------------------------------------------------------
 * Event-Bindungen
 *
 * Ersetzen die früheren Inline-Aufrufe in product-form.js, cart-drawer.js,
 * layout/theme.liquid und sections/main-cart.liquid. Genutzt werden die von
 * Enterprise dokumentierten Theme-Events (Liste in assets/custom.js), damit
 * diese vier Vendor-Dateien bei Theme-Updates unangetastet bleiben.
 * ------------------------------------------------------------------------- */
(function () {
  var API = window.SAnalytics;
  if (!API) return;

  function cartUrl() {
    var routes = (window.theme && window.theme.routes) || {};
    return (routes.cart || '/cart') + '.js';
  }

  /**
   * Die Theme-Events liefern nur eine variantId, nicht die /cart/add.js-Response,
   * die pushAddToCartDataLayer erwartet. Deshalb die Zeile aus dem Warenkorb
   * nachladen und auflösen.
   * @param {number|string} variantId
   * @param {number} [quantity] - Hinzugefügte Menge; ohne Angabe die Zeilenmenge.
   */
  function trackAdd(variantId, quantity) {
    if (!variantId) return;
    fetch(cartUrl(), { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var line = (cart.items || []).filter(function (item) {
          return String(item.variant_id) === String(variantId);
        })[0];
        if (line) API.productAddedToCart(line, quantity || line.quantity);
      })
      .catch(function () {});
  }

  /**
   * cart-items.js feuert on:line-item:change auch für die Mengen-Stepper im
   * Warenkorb. Das ist kein add_to_cart und würde die Kennzahl aufblähen, also
   * nur Events aus einem Produktformular zählen.
   */
  function fromProductForm(event) {
    var el = event.target;
    return !!(el && el.closest && el.closest('product-form'));
  }

  document.addEventListener('on:cart:add', function (event) {
    if (!fromProductForm(event)) return;
    trackAdd(event.detail && event.detail.variantId);
  });

  document.addEventListener('on:line-item:change', function (event) {
    if (!fromProductForm(event)) return;
    var detail = event.detail || {};
    var delta = Number(detail.newQuantity) - Number(detail.oldQuantity);
    if (delta > 0) trackAdd(detail.variantId, delta);
  });

  document.addEventListener('on:cart-drawer:after-open', function () {
    API.viewCartFetch();
  });

  // view_cart beim Laden der Warenkorbseite. Das Skript lädt mit defer, das DOM
  // steht hier also. <cart-items> gibt es auch im Drawer, daher die Abgrenzung.
  var cartItems = document.querySelector('cart-items');
  if (cartItems && !cartItems.closest('cart-drawer') && cartItems.dataset.empty !== 'true') {
    API.viewCartFetch();
  }
}());
